import { useEffect, useState } from "react";

// A tab opened before a deploy still asks for the old build's code chunks, which the
// new deploy no longer serves. Reloading loads the new build; a short guard keeps a
// real outage from reloading in a loop.

const STALE_BUILD =
  /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS/i;
const RELOADED_AT_KEY = "ge.staleBuildReloadedAt";
const RELOAD_GUARD_MS = 60_000;

export function isStaleBuildError(error: unknown): boolean {
  return error instanceof Error && STALE_BUILD.test(error.message);
}

type ReloadOptions = {
  storage?: Pick<Storage, "getItem" | "setItem">;
  now?: number;
  reload?: () => void;
};

function reloadedRecently({ storage = window.sessionStorage, now = Date.now() }: ReloadOptions) {
  const last = Number(storage.getItem(RELOADED_AT_KEY));
  return Boolean(last) && now - last < RELOAD_GUARD_MS;
}

/** Reloads the page unless it already did within the last minute; says whether it did. */
export function reloadForNewBuild(options: ReloadOptions = {}): boolean {
  const {
    storage = window.sessionStorage,
    now = Date.now(),
    reload = () => window.location.reload(),
  } = options;
  if (reloadedRecently({ storage, now })) return false;
  storage.setItem(RELOADED_AT_KEY, String(now));
  reload();
  return true;
}

/**
 * For error pages: when the error is a stale build, reloads into the new one and
 * says so, so the page can show nothing instead of an error that is about to vanish.
 */
export function useReloadForNewBuild(error: unknown): boolean {
  const [reloading] = useState(() => isStaleBuildError(error) && !reloadedRecently({}));
  useEffect(() => {
    if (reloading) reloadForNewBuild();
  }, [reloading]);
  return reloading;
}
